
import {AccessoryService} from "../components/accessoryComponent/accessoryService";
import {PacketService} from "../components/packetComponent/packetService";
import {ProductDataStore} from "../stores/productDataStore";
import {HingeSideService} from "./hingeSideService";
import {DrawingDataService} from "../components/drawingComponent/drawingDataService";

export class AccessoryRuleService {
    private productDataStore: ProductDataStore;
    private drawingDataService: DrawingDataService;

    private rules: any;
    private availableAccessories: any;
    private selectedAccessories: any;
    private availablePackets: any;
    private selectedPackets: any;
    private isTwoSash: boolean;
    private isTilt: boolean;
    private mainFunction: any;
    private material: any;
    private width: number;
    private height: number;
    private minSizes: any;
    private readonly accessoryTypeValidationRules = {};
    private valgeDoorBrakeAccessoriesIds = [
        800,
        801
    ];

    constructor() {
        this.productDataStore = new ProductDataStore();
        this.drawingDataService = new DrawingDataService();
        this.accessoryTypeValidationRules = {
            2: 'fixInFixRules',
            3: 'rollerShutterRules',
            4: 'blindRules',
            5: 'cutForWindowSillRules',
            6: 'slotVentiltorRules',
            7: 'hiddenHingesRules',
            8: 'visibleHingesRules',
            10: 'brakeInHandleRules',
            11: 'selfClosingSystemRules',
            12: 'frictionBrakeRules',
            13: 'lowThresholdRules',
            25: 'otherAccessoriesRules',
        };
    }

    private refreshConstructionData() {
        const sashes = this.productDataStore.getSashConfiguration();
        const configuration = this.productDataStore.getData();

        this.rules = configuration.rules;
        this.isTwoSash = Number(this.productDataStore.getAttributeByName('standardFields')) === 2;
        this.width = Number(this.productDataStore.getAttributeByName('width'));
        this.height = Number(this.productDataStore.getAttributeByName('height'));
        this.material = this.productDataStore.getProductInfoAttributeByName('productMaterial');
        this.availableAccessories = this.productDataStore.getAvailableOptionsByName('accessory');
        this.selectedAccessories = this.productDataStore.getSelectedOption('accessories', 'accessory');
        this.availablePackets = this.productDataStore.getAvailableOptionsByName('packet');
        this.selectedPackets = this.productDataStore.getSelectedOption('packet', 'packet');
        this.isTilt = Number(this.productDataStore.getSelectedOptionIdByName('hingeSide')) === HingeSideService.BOTTOM_HINGE_ID ||
            (
                this.rules.turn_tilt &&
                Number(this.productDataStore.getAttributeByName('standardFields')) === 1 &&
                sashes.sashes.length > 0 &&
                sashes.sashes[0].handlePosition === 'T'
            );

        this.mainFunction = null;

        if (this.isTwoSash) {
            this.mainFunction = this.rules.handle_quantity === 2 ? 'fixed_mullion' : 'floating_mullion';
        } else if (this.rules.side_hung) {
            this.mainFunction = 'side_hung';
        } else if (this.isTilt) {
            this.mainFunction = 'tilt';
        } else {
            this.mainFunction = 'turn_tilt';
        }
        this.minSizes = this.rules.min_sizes ? {
            hidden: this.rules.min_sizes.hidden[this.mainFunction],
            visible: this.rules.min_sizes.visible[this.mainFunction]
        } : null;
    }

    public fixInFixRules() {
        if (this.rules.type < AccessoryService.accessoryTypesIds.fixInFix) {
            return true;
        }
        if (!this.rules.fix && !this.rules.turn_tilt) {
            return true;
        }
        return this.minSizes &&
            this.minSizes.hidden.height <= this.height &&
            this.minSizes.hidden.width <= this.width;
    }

    public rollerShutterRules() {
        if (this.rules.type < AccessoryService.accessoryTypesIds.fixInFix) {
            return true;
        }
        return this.width >= 400 &&
            !AccessoryService.hasSelectedAccessoryWithType(
                AccessoryService.accessoryTypesIds.slotVentilator
            );
    }

    public blindRules() {
        if (this.rules.type < AccessoryService.accessoryTypesIds.fixInFix) {
            return true;
        }
        return ((this.material === 'pvc' && this.width >= 450) || this.width >= 250) &&
            this.width <= 2200 &&
            this.width * this.height <= 2500000 &&
            (
                !AccessoryService.hasSelectedAccessoryWithType(
                    AccessoryService.accessoryTypesIds.slotVentilator
                ) ||
                this.rules.window
            );
    }

    public cutForWindowSillRules() {
        if (this.rules.type < AccessoryService.accessoryTypesIds.fixInFix) {
            return true;
        }
        return !AccessoryService.hasSelectedAccessoryWithType(
            AccessoryService.accessoryTypesIds.lowThreshold
        );
    }

    public slotVentiltorRules() {
        if (this.rules.type < 0) {
            return true;
        }
        const result = !this.isTilt && (!this.rules.window || this.width >= 501);

        return result &&
            !AccessoryService.hasSelectedAccessoryWithType(
                AccessoryService.accessoryTypesIds.rollerShutter
            ) &&
            (
                !AccessoryService.hasSelectedAccessoryWithType(
                    AccessoryService.accessoryTypesIds.blind
                ) || this.rules.window
            );
    }

    public hiddenHingesRules() {
        if (this.rules.type < AccessoryService.accessoryTypesIds.fixInFix) {
            return true;
        }

        if (this.balconyDoorsRuleForHiddenHinges()) {
            return false;
        }

        return this.dimensionsRuleForHiddenHinges();
    }

    public balconyDoorsRuleForHiddenHinges() {
        return (
            AccessoryService.hasSelectedAccessoryWithType(
                AccessoryService.accessoryTypesIds.lowThreshold
            ) &&
            this.rules.eligible_for_balcony_doors_rule
        );
    }

    public dimensionsRuleForHiddenHinges() {
        if (this.rules.window_door || this.rules.fix || this.rules.turn_tilt) {
            if (this.rules.window_door && this.rules.openable_outwards) {
                if (this.isTwoSash) {
                    return this.width >= 1100;
                }

                return this.width >= 600;
            }

            if (this.minSizes) {
                return this.minSizes &&
                    this.minSizes.hidden.height <= this.height &&
                    this.minSizes.hidden.width <= this.width;
            }
        }

        return true;
    }

    public visibleHingesRules() {
        if (this.rules.type < AccessoryService.accessoryTypesIds.fixInFix) {
            return true;
        }

        if (this.rules.window_door || this.rules.fix || this.rules.turn_tilt) {
            if (this.rules.window_door && this.rules.openable_outwards) {
                if (this.isTwoSash) {
                    return this.width >= 1150;
                }

                return this.width >= 590;
            }

            return this.minSizes &&
                this.minSizes.visible.height <= this.height &&
                this.minSizes.visible.width <= this.width;
        }

        return true;
    }

    public brakeInHandleRules() {
        if (this.rules.type < AccessoryService.accessoryTypesIds.fixInFix) {
            return true;
        }

        if (this.rules.window_door && this.rules.openable_outwards) {
            if (this.isTwoSash) {
                if (AccessoryService.hasSelectedAccessoryWithType(
                    AccessoryService.accessoryTypesIds.hiddenHinges
                )) {
                    return this.width >= 1890;
                }
                return this.width >= 1290;
            }

            if (AccessoryService.hasSelectedAccessoryWithType(
                AccessoryService.accessoryTypesIds.hiddenHinges
            )) {
                return this.width >= 930;
            }
            return this.width >= 680;
        }

        return true;
    }

    public selfClosingSystemRules() {
        if (this.rules.type < 0) {
            return true;
        }

        return !this.rules.psk || this.width >= 1801;
    }

    public frictionBrakeRules() {
        if (this.rules.type < 2 || this.material !== 'pvc' || !this.rules.openable_outwards) {
            return true;
        }

        return this.isTwoSash ? this.width >= 1500 : this.width >= 750;
    }

    public lowThresholdRules() {
        if (this.rules.type < 2) {
            return true;
        }

        if (this.balconyDoorsRuleForLowThreshold()) {
            return false;
        }

        return !AccessoryService.hasSelectedAccessoryWithType(
            AccessoryService.accessoryTypesIds.cutForWindowSill
        );
    }

    public balconyDoorsRuleForLowThreshold() {
        return (
            AccessoryService.hasSelectedAccessoryWithType(
                AccessoryService.accessoryTypesIds.hiddenHinges
            ) &&
            this.rules.eligible_for_balcony_doors_rule
        );
    }

    public otherAccessoriesRules() {
        if (this.rules.eligible_for_valge_doors_rule &&
            this.selectedAccessories.find(
                accessory => this.valgeDoorBrakeAccessoriesIds.includes(
                    accessory.id
                )
            )
        ) {
            return this.width >= 770;
        }

        return true;
    }

    public requireDivider() {
        if (this.rules.window_door && this.material === 'pvc' && !this.drawingDataService.hasBottomPanel()) {
            if (this.isTwoSash) {
                return this.width > 2000;
            }
            return this.width > 1000;
        }
        return false;
    }

    public validate() {
        this.refreshConstructionData();

        let selectedAccessories = this.selectedAccessories;
        let selectedPackets = this.selectedPackets;

        const partsStatus = {
            // requireDivider: this.requireDivider(),
            accessories: {},
            packets: {}
        };
        const hiddenHinge = AccessoryService.getSelectedAccessoriesWithType(AccessoryService.accessoryTypesIds.hiddenHinges);
        partsStatus.accessories = this.validateHiddenHinge(hiddenHinge);

        if (Object.entries(partsStatus.accessories).length > 0) {
            selectedAccessories.push(hiddenHinge);
        }
        const accessoriesToValidate = this.selectedAccessories;

        if (hiddenHinge) {
            accessoriesToValidate.push(hiddenHinge);
        }
        const validHinge = hiddenHinge && this.validateAccessories(accessoriesToValidate);

        if (hiddenHinge) {
            partsStatus.accessories[hiddenHinge.id] = validHinge;
        }

        selectedAccessories = selectedAccessories.filter((accessory) => {
            const accessoryOptions = {
                ...this.selectedAccessories,
                selectedAccessories: [
                    accessory
                ]
            };
            if (validHinge) {
                accessoryOptions.selectedAccessories.push(hiddenHinge);
            }
            partsStatus.accessories[accessory.id] =
                partsStatus.accessories[accessory.id] !== false &&
                this.validateAccessories(accessoryOptions.selectedAccessories);

            return partsStatus.accessories[accessory.id];
        });

        selectedPackets = selectedPackets.filter((packet) => {
            const accessoryOptions = {
                ...this.selectedAccessories,
                selectedAccessories: PacketService.getPacketAccessories(packet)
            };
            if (validHinge) {
                accessoryOptions.selectedAccessories.push(hiddenHinge);
            }
            const packetOptions = {
                ...this.selectedPackets,
                selectedPackets: [
                    ...selectedPackets,
                    packet
                ]
            };
            partsStatus.packets[packet.id] =
                this.validateAccessories(accessoryOptions.selectedAccessories) &&
                this.validatePackets(packetOptions.selectedPackets);

            return partsStatus.packets[packet.id];
        });

        this.availableAccessories.forEach((accessory) => {
            partsStatus.accessories[accessory.id] = partsStatus.accessories[accessory.id] !== false &&
                this.validateAccessories([
                    ...selectedAccessories,
                    accessory
                ]);
        });
        this.availablePackets.forEach((packet) => {
            partsStatus.packets[packet.id] = partsStatus.packets[packet.id] !== false &&
                this.validateAccessories([
                    ...selectedAccessories,
                    ...PacketService.getPacketAccessories(packet)
                ]) &&
                this.validatePackets([
                    ...this.selectedPackets,
                    packet
                ]);
        });

        return partsStatus;
    }

    private validateHiddenHinge(hiddenHinge) {
        const validHinge = hiddenHinge && this.validateAccessories([hiddenHinge]);
        const validAccessoriesIds = {};

        if (hiddenHinge) {
            validAccessoriesIds[hiddenHinge.id] = validHinge;
        }
        return validAccessoriesIds;
    }

    private validateAccessories(accessories) {
        if (!accessories) {
            return true;
        }
        return accessories.every((accessory) => {
            if (
                !accessory.attributes.allowMultiple &&
                !accessory.attributes.isDefault &&
                accessory.attributes.price !== 0
            ) {
                if (this.hasSelectedOtherAccessoriesWithSameType(accessory)) {
                    return false;
                }
            }
            if (this.accessoryTypeValidationRules[accessory.attributes.accessoryType]) {
                return this[this.accessoryTypeValidationRules[accessory.attributes.accessoryType]]();
            }
            return true;
        });
    }

    private validatePackets(packets) {
        return packets.every(
            packet => (
                packet.attributes.price == null ||
                packet.attributes.price > 0 ||
                packet.id <= 0
            ) &&
            !this.hasSelectedOtherPacketsWithSameType(packet)
        );
    }

    private hasSelectedOtherPacketsWithSameType(packet) {
        return this.selectedPackets.some(
            selectedPacket => Number(selectedPacket.id) !== Number(packet.id) &&
            Number(selectedPacket.attributes.packetType) === Number(packet.attributes.packetType)
        );
    }

    private hasSelectedOtherAccessoriesWithSameType(accessory) {
        return this.selectedAccessories.some(
            selectedAccessory => Number(selectedAccessory.id) !== Number(accessory.id) &&
            Number(selectedAccessory.attributes.accessoryType) === Number(accessory.attributes.accessoryType)
        );
    }
}
